
import React, { useState, useEffect } from 'react';
import { MapPinIcon } from '../icons/WeatherIcons';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';

interface TimerHeaderProps {
  currentTime: Date;
}

const weatherDescriptions: { [key: number]: string } = {
  0: "Clear ☀️",
  1: "Mainly Clear 🌤️",
  2: "Partly Cloudy ⛅",
  3: "Overcast ☁️",
  45: "Fog 🌫️",
  48: "Dense Fog 🌫️",
  51: "Light Drizzle 🌦️",
  53: "Moderate Drizzle 🌦️",
  55: "Dense Drizzle 🌦️",
  56: "Freezing Drizzle 🌨️",
  57: "Dense Freezing Drizzle 🌨️",
  61: "Light Rain 🌧️",
  63: "Moderate Rain 🌧️",
  65: "Heavy Rain 🌧️",
  66: "Freezing Rain 🌨️",
  67: "Heavy Freezing Rain 🌨️",
  71: "Light Snow ❄️",
  73: "Moderate Snow ❄️",
  75: "Heavy Snow ❄️",
  77: "Snow Grains ❄️",
  80: "Light Rain Showers 🌦️",
  81: "Moderate Rain Showers 🌦️",
  82: "Heavy Rain Showers 🌧️",
  85: "Light Snow Showers ❄️",
  86: "Heavy Snow Showers ❄️",
  95: "Thunderstorm ⛈️",
  96: "Thunderstorm with Hail ⛈️",
  99: "Heavy Thunderstorm with Hail ⛈️"
};

interface OpenMeteoResponse {
  current: {
    temperature_2m: number;
    weather_code: number;
  };
}

const getPrettyLocation = (geo: any): string => {
  // Prioritize city/town/village, then fallback to county/state/country
  if (!geo || !geo.address) return "Your Location";
  const address = geo.address;
  return (
    address.city ||
    address.town ||
    address.village ||
    address.hamlet ||
    address.municipality ||
    address.state_district ||
    address.state ||
    address.county ||
    address.country ||
    "Your Location"
  ) + (address.country && address.country !== (address.city || address.town || address.village) ? ", " + address.country : "");
};

const fetchReverseGeocodedLocation = async (lat: number, lon: number): Promise<string> => {
  // Try to use sessionStorage for caching
  const cacheKey = `lovable-location-${lat.toFixed(3)}-${lon.toFixed(3)}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const { data, error } = await supabase.functions.invoke('geocode', {
      body: { latitude: lat, longitude: lon }
    });

    if (error) throw error;
    
    const location = data?.location || "Your Location";
    if (location && location !== "Your Location") {
      sessionStorage.setItem(cacheKey, location);
    }
    return location;
  } catch (e) {
    console.error('Geocoding error:', e);
    return "Your Location";
  }
};

const TimerHeader: React.FC<TimerHeaderProps> = ({ currentTime }) => {
  const [location, setLocation] = useState<string>("Loading location...");
  const [temperature, setTemperature] = useState<number | null>(null);
  const [weatherCode, setWeatherCode] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isMobile = useIsMobile();

  // Format time and date with day of week
  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });

  const formattedDate = currentTime.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

  useEffect(() => {
    // Don't fetch on mobile
    if (isMobile) return;

    if (!("geolocation" in navigator)) {
      setError("Weather unavailable – Location permission denied");
      setLocation("Weather unavailable");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;

        // Load city/country via reverse geocode (Nominatim)
        setLocation("Loading location...");
        let prettyLocation = "Your Location";
        try {
          prettyLocation = await fetchReverseGeocodedLocation(latitude, longitude);
        } catch {
          prettyLocation = "Your Location";
        }
        setLocation(prettyLocation);

        // Fetch weather
        try {
          const { data, error } = await supabase.functions.invoke('weather', {
            body: { latitude, longitude }
          });

          if (error) throw error;
          
          setTemperature(data?.temperature || null);
          setWeatherCode(data?.weatherCode ?? null);
        } catch (e) {
          console.error('Weather error:', e);
          setError("Weather unavailable – Failed to fetch weather");
        }
      },
      (geoError) => {
        setError("Weather unavailable – Location permission denied");
        setLocation("Weather unavailable");
      }
    );
  }, [isMobile]);

  // No display on mobile
  if (isMobile) return null;

  // Decide which weather string to show
  let weatherString = "";
  if (weatherCode !== null) {
    weatherString =
      weatherDescriptions[weatherCode] ||
      `Code ${weatherCode}`;
  }

  // Compose the display line (location + weather)
  const locationDisplay = error
    ? error
    : (
      <>
        <MapPinIcon size={12} className="mr-1" />
        {location}
        {(weatherString && temperature != null) && (
          <span className="flex items-center ml-2">
            <span className="ml-0.5">{weatherString}</span>
            <span className="ml-1">{Math.round(temperature)}°C</span>
          </span>
        )}
      </>
    );

  return (
    <div
      className="fixed text-right p-4 text-white font-light"
      style={{
        top: "100px",
        right: "20px",
        zIndex: 5,
        textShadow: "0 1px 3px rgba(0, 0, 0, 0.7)"
      }}
    >
      <div className="text-sm flex items-center justify-end mb-1">
        {locationDisplay}
      </div>
      <div className="text-sm mb-1">{formattedDate}</div>
      <div className="text-2xl font-light font-inter">{formattedTime}</div>
    </div>
  );
};

export default TimerHeader;
